import { AppSelectBox } from 'components';
import SelectBoxV2 from 'components/Inputs/SelectBoxV2';
import { Fragment, forwardRef } from 'react';

/**
 * PodcastDropdown
 * Reusable podcast picker — mirrors PodcastCategoryDropdown pattern.
 *
 * Props:
 *   value        — selected value (_id string)
 *   handleChange — (event, option) => void
 *   options      — [{ label, value }]  (from usePodcastDropdown)
 *   label        — field label string (default: "Podcast")
 *   version      — "v2" → react-select (SelectBoxV2), else plain AppSelectBox
 *   isLoading    — show loading state
 *   isError      — show error state
 *   defaultLook  — passed through to AppSelectBox (non-v2 only)
 */
function PodcastDropdown(
  {
    options = [],
    value,
    handleChange,
    label = 'Podcast',
    labelClass,
    version,
    isLoading = false,
    isError = false,
    fieldErrors = {},
    defaultLook = true,
    onKeyDown,
    onFocus,
    tabIndex,
    placeholder = '— Select podcast —',
    ...otherProps
  },
  ref
) {
  const handleKeyDown = (e) => {
    if (onKeyDown) onKeyDown(e);
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (e.target?.click) e.target.click();
    }
  };

  if (version === 'v2') {
    const selectedOption = options.find((o) => o.value === value) ?? null;

    return (
      <SelectBoxV2
        labelProps={{ label, className: labelClass }}
        fieldErrors={fieldErrors}
        selectProps={{
          isLoading,
          options,
          value: selectedOption,
          placeholder,
          getOptionLabel: (opt) => opt.label,
          getOptionValue: (opt) => opt.value,
          isClearable: true,
          onChange: (opt) => {
            handleChange({ target: { value: opt ? opt.value : '' } }, opt);
          },
          onKeyDown: handleKeyDown,
          onFocus: (e) => {
            if (onFocus) onFocus(e);
          },
          tabIndex,
          ...otherProps,
        }}
      />
    );
  }

  // Plain AppSelectBox version (used inside tables / compact UI)
  return (
    <AppSelectBox
      labelProps={{ label, className: labelClass }}
      selectProps={{
        value,
        onChange: (e) => {
          const sel = e.target.value;
          const opt = options.find((o) => o.value === sel) || null;
          handleChange(e, opt);
        },
        onKeyDown: handleKeyDown,
        onFocus: (e) => {
          if (onFocus) onFocus(e);
        },
        tabIndex,
        ...otherProps,
      }}
      fieldErrors={fieldErrors}
      defaultLook={defaultLook}
    >
      {isLoading ? (
        <option disabled value="">
          Loading…
        </option>
      ) : isError ? (
        <option disabled value="">
          Failed to load
        </option>
      ) : (
        <Fragment>
          <option value="">— None —</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Fragment>
      )}
    </AppSelectBox>
  );
}

PodcastDropdown.defaultProps = {
  handleChange: () => {},
};

export default forwardRef(PodcastDropdown);
